import { useCallback, useContext, useState } from 'react';
import { AjaxContext, MerchantContext } from 'commons-ui';
import type { Endpoints, ForwardRoutes, ServiceOptions } from './types';

export const buildBulkRequestService = (endpoints: Endpoints, forwardRoutes: ForwardRoutes, options: ServiceOptions) => {
	return function useBulkRequestService() {
		const { postData } = useContext(AjaxContext);
		const { merchantName } = useContext(MerchantContext);
		const [forceUpdate, setForceUpdate] = useState(false);

		const turnForceUpdateOff = () => setForceUpdate(false);

		const listRequests = useCallback(
			async ({ pageDetails }: { pageDetails: { pageNo: number; pageSize: number } }) => {
				const response = await postData({
					endpoint: endpoints.LIST_REQUESTS,
					body: {
						pageNo: pageDetails.pageNo,
						pageSize: pageDetails.pageSize,
						userMerchant: merchantName,
						forwardReqRoute: forwardRoutes.LIST_REQUESTS,
						forwardReqToService: options.SERVICE_NAME
					}
				});

				if (!response?.data?.success) {
					throw new Error(response?.data?.message || 'Failed to fetch requests');
				}

				const requests = response.data.data.requests || [];
				const indexedRequests = requests.map((req: any, idx: number) => ({ ...req, index: idx + 1 }));

				return {
					rowCount: response.data.data.totalCount,
					data: indexedRequests
				};
			},
			[postData, merchantName]
		);

		const checkStatus = useCallback(
			(requestId: string) =>
				async ({ pageDetails }: { pageDetails: { pageNo: number; pageSize: number } }) => {
					const response = await postData({
						endpoint: endpoints.CHECK_STATUS,
						body: {
							requestId,
							pageNo: pageDetails.pageNo,
							pageSize: pageDetails.pageSize,
							forwardReqRoute: forwardRoutes.CHECK_STATUS,
							forwardReqToService: options.SERVICE_NAME
						}
					});

					if (!response?.data?.success) {
						throw new Error(response?.data?.message || 'Failed to fetch status');
					}

					const items = response.data.data.items || [];
					const indexedItems = items.map((item: any, idx: number) => ({ ...item, index: idx + 1 }));

					return {
						rowCount: response.data.data.totalCount,
						data: indexedItems
					};
				},
			[postData]
		);

		const createRequest = useCallback(
			async (payload: any) => {
				const response = await postData({
					endpoint: endpoints.CREATE_REQUEST,
					body: {
						...payload,
						userMerchant: merchantName,
						forwardReqRoute: forwardRoutes.CREATE_REQUEST,
						forwardReqToService: options.SERVICE_NAME
					}
				});

				if (!response?.data?.success) {
					throw new Error(response?.data?.message || 'Failed to create request');
				}

				return response.data.data;
			},
			[postData, merchantName]
		);

		return {
			listRequests,
			checkStatus,
			createRequest,
			forceUpdate,
			turnForceUpdateOff
		};
	};
};
